import { DownOutlined } from '@ant-design/icons'
import { Button, Dropdown, Space } from 'antd'
import Quill from 'quill'
import { Delta } from 'quill/core'
import 'quill/dist/quill.snow.css'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { uniqueId } from '../../helpers/uniqueId'
import { insertInlineImage } from './insertInlineImage'
import './tokenBlot'
import './tokenBlot.css'
import { Token, convertFromHandlebars, convertToHandlebars } from './tokenModel'
import './richTextEditor.css'

export type Props = {
  disabled?: boolean
  value?: string
  onChange?: (value: string, state?: any) => void
  state?: any,
  tokens?: Token[],
  removeToolbar?: boolean
  singleLine?: boolean
  style?: React.CSSProperties
  placeholder?: string
  allowImages?: boolean
}

const RichTextEditor = ({ disabled, value, onChange, state, tokens = [], removeToolbar = false, singleLine = false, style, placeholder, allowImages = false }: Props) => {
  const quillRef = useRef<Quill | undefined>()
  const containerRef = useRef<any>()
  const onChangeRef = useRef(onChange)
  const idRef = useRef<string | undefined>(uniqueId())
  const htmlRef = useRef<string | undefined>()

  useLayoutEffect(() => {
    onChangeRef.current = onChange
  })

  const modules = {
    toolbar: removeToolbar || disabled ? false : `#toolbar-${idRef.current}`,
    ...(singleLine ? {
      keyboard: {
        bindings: {
          enter: {
            key: 'Enter',
            handler: () => false
          },
          enterKeyCode: {
            key: 13,
            handler: () => false
          }
        }
      },
      clipboard: {
        matchers: [
          [Node.ELEMENT_NODE, (node: any, delta: any) => {
            const isCodeElement = node.nodeName.toLowerCase() === 'code'
            if (!isCodeElement) {
              const newText = node.textContent?.replace(/\n/g, '') ?? ''
              const newDelta = new Delta()
              return newDelta.insert(newText)
            }
            return delta
          }]
        ]
      }
    } : {})
  }

  const formats = [
    ...(!singleLine ? ['bold', 'italic', 'underline', 'strike', 'list', 'indent', 'link'] : []),
    ...(allowImages ? ['image'] : []),
    'token'
  ]

  useEffect(() => {
    const container = containerRef.current
    const editorContainer = container.appendChild(container.ownerDocument.createElement('div'))
    editorContainer.setAttribute('id', `editor-${idRef.current}`)

    const quill = new Quill(editorContainer, { theme: 'snow', modules, formats, placeholder })

    if (allowImages) {
      const toolbarModule = quill.getModule('toolbar') as { addHandler: (name: string, handler: () => void) => void } | undefined
      toolbarModule?.addHandler('image', () => insertInlineImage(quill))
    }
    quillRef.current = quill

    quill.on('text-change', () => {
      const newValue = convertToHandlebars(quill.getSemanticHTML(), singleLine)
      htmlRef.current = newValue
      if (newValue !== value) {
        onChangeRef.current?.(newValue, state)
      }
    });

    return () => {
      quillRef.current = undefined
      container.innerHTML = ''
    }
  }, [])

  useEffect(() => {
    quillRef.current?.enable(!disabled)
  }, [disabled])

  useEffect(() => {
    const quill = quillRef.current
    if (!quill) {
      return
    }

    if (value !== htmlRef.current) {
      const selection = quill.getSelection()
      const newValue = convertFromHandlebars(value, tokens ?? [])
      const timeout = setTimeout(() => {
        quill.setContents(quill.clipboard.convert({ html: newValue }), 'silent')
        htmlRef.current = value
        if (selection) {
          quill.setSelection(selection, 'silent')
        }
      }, 0)
      return () => clearTimeout(timeout)
    }
  }, [value, quillRef.current])

  const insertToken = (token: { label: string, value: string }) => (e: any) => {
    e.preventDefault()
    const quill = quillRef.current!
    const selection = quill.getSelection(true)
    quill.insertEmbed(selection.index, 'token', token, 'user')
    quill.setSelection(selection.index + 1, selection.length, 'silent')
  }


  return <div className={disabled ? 'quill-disabled' : ''} style={style}>
    {!removeToolbar && !disabled &&
      <div id={`toolbar-${idRef.current}`} className="ql-toolbar ql-snow" tabIndex={-1}>
        {!singleLine && <>
          <button className="ql-bold" />
          <button className="ql-italic" />
          <button className="ql-underline" />
          <button className="ql-strike" />
          <button className="ql-list" value="ordered" />
          <button className="ql-list" value="bullet" />
          <button className="ql-indent" value="+1" />
          <button className="ql-indent" value="-1" />
          <button className="ql-link" />
          {allowImages && <button className="ql-image" />}
          <button className="ql-clean" />
        </>}
        {tokens.length > 0 &&
          <Dropdown trigger={["click"]} menu={{
            items: tokens.map(t => ({
              key: t.value,
              label: <a onClick={insertToken(t)}>{t.label}{t.description && <small><br />Example: {t.description}</small>}</a>
            }))
          }}
          >
            <Button tabIndex={-1}>
              <Space>
                Add Placeholder
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
        }
      </div>
    }
    <div ref={containerRef} style={{ height: singleLine ? 48 : undefined }}></div>
  </div>

}

export default RichTextEditor
